import { isDefined } from "@clipboard-health/util-ts";
import { Menu, MenuItem } from "@mui/material";
import { useState } from "react";

import { type IconType } from "../../components/Icon";
import { IconButton, type IconButtonProps } from "../../components/IconButton";
import { Text } from "../../components/Text";
import { useTu } from "@src/appV2/i18n/useTu";

export interface MoreMenuItem {
  label: string;
  onClick: () => void;
  visible: boolean;
  disabled?: boolean;
}

interface ProfessionalMoreMenuProps {
  items: MoreMenuItem[];
  triggerIconType?: IconType;
  triggerButtonProps?: Partial<IconButtonProps>;
  onOpenChange?: (isOpen: boolean) => void;
}

export function ProfessionalMoreMenu(props: ProfessionalMoreMenuProps) {
  const { tu } = useTu("myProfessionals");
  const { items, triggerIconType = "ellipsis", triggerButtonProps, onOpenChange } = props;
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const isOpen = isDefined(anchorElement);

  const visibleItems = items.filter((item) => item.visible);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        iconType={triggerIconType}
        variant="outlined"
        size="medium"
        aria-label={tu("More actions")}
        aria-haspopup="true"
        {...triggerButtonProps}
        onClick={(event) => {
          setAnchorElement(event.currentTarget as HTMLElement);
          onOpenChange?.(true);
        }}
      />
      <Menu
        anchorEl={anchorElement}
        open={isOpen}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          sx: {
            paddingY: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              minWidth: "280px",
              marginTop: "5px",
              borderRadius: theme.borderRadius?.xLarge,
              border: `1px solid ${theme.palette.border?.base}`,
              boxShadow: theme.shadow?.card,
              overflow: "hidden",
            }),
          },
        }}
        onClose={() => {
          setAnchorElement(undefined);
          onOpenChange?.(false);
        }}
      >
        {visibleItems.map((item) => (
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            sx={(theme) => ({
              minHeight: "68px",
              paddingX: theme.spacing(5),
              paddingY: theme.spacing(4),
              justifyContent: "flex-start",
            })}
            onClick={() => {
              item.onClick();
              setAnchorElement(undefined);
              onOpenChange?.(false);
            }}
          >
            <Text variant="body1">{item.label}</Text>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
